import React, { type FC } from 'react'
import { styled } from '@linaria/react'
import { colors } from '../styles/colors'
import { CHAIN } from '@tonconnect/protocol'
import { useTonWallet, useTonConnectUI } from '@tonconnect/ui-react'
import { useTonClient } from '../hooks/useTonClient'
import { useWalletBalance } from '../hooks/useWalletBalance'
import WalletIcon from '../svgr/Wallet'
import { ButtonRegular } from '../ui/Button'
import { FormattedMessage } from 'react-intl'
import { fromNano } from '@ton/core'
import { Typography, fontSize } from '../ui/Typography'
import TonIcon from '../svgr/TonIcon'
import { useTelegram } from '../hooks/useTelegram'
import { shortenWalletAddress } from '../functions/shortenWalletAddress'
import { trackWalletConnectionStarted } from '../utils/amplitude'

export const Wallet: FC<{
  className?: string
  isAvatarFirst?: boolean
}> = ({ className, isAvatarFirst = false }) => {
  const wallet = useTonWallet()
  const { tg } = useTelegram()
  const [tonConnectUI] = useTonConnectUI()
  const { client } = useTonClient()
  const balance = useWalletBalance(client)

  const handleConnect = async () => {
    try {
      trackWalletConnectionStarted()
      await tonConnectUI.openModal()
    } catch (error) {
      console.error('Failed to connect wallet:', error)
    }
  }

  if (!wallet) {
    return (
      <WalletContainer className={className}>
        <ConnectButton
          onClick={handleConnect}
          fontSize={12}
          textWeight={700}
          textColor={colors.secondary}
        >
          <ButtonContent>
            <WalletIcon />

            <FormattedMessage
              id="wallet.connect"
              defaultMessage="Подключить кошелек"
            />
          </ButtonContent>
        </ConnectButton>
      </WalletContainer>
    )
  }

  const telegramUser = tg.initDataUnsafe.user
  const userPhoto = telegramUser?.photo_url ?? '/placeholder.webp'
  const isTestnet = wallet.account.chain === CHAIN.TESTNET
  const walletAddress = shortenWalletAddress(wallet.account.address, isTestnet)

  const formatBalance = (balance: bigint | null): string => {
    if (balance === null) {
      return '-'
    }
    const tonBalance = fromNano(balance)
    return Number(tonBalance).toFixed(2)
  }

  return (
    <WalletContainer className={className}>
      <UserInfo isAvatarFirst={isAvatarFirst}>
        <Typography
          fontSize={12}
          fontWeight={600}
          color={colors.secondaryActive}
        >
          {walletAddress}
        </Typography>

        <AmountContainer
          fontSize={10}
          fontWeight={400}
          color={colors.grey700}
          font="roboto"
        >
          {formatBalance(balance)}
          <TonIcon />
        </AmountContainer>

        <AvatarImage
          src={userPhoto}
          alt={walletAddress}
          width={30}
          height={30}
          isAvatarFirst={isAvatarFirst}
        />
      </UserInfo>
    </WalletContainer>
  )
}

const WalletContainer = styled.div`
  display: flex;
  align-items: center;
`

const ConnectButton = styled(ButtonRegular)`
  border: 1px solid ${colors.secondary};
  border-radius: 100px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: 360px) {
    padding: 6px 10px;
    font-size: ${fontSize[10]};
  }

  @media (max-width: 340px) {
    padding: 6px 8px;
    font-size: ${fontSize[10]};
  }
`

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-gap: 5px;

  @media (max-width: 360px) {
    gap: 3px;
    flex-gap: 3px;
  }
`

const AmountContainer = styled(Typography)`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-gap: 4px;

  & svg {
    width: 10px;
    height: 10px;
  }
`

const UserInfo = styled.div<{ isAvatarFirst: boolean }>`
  display: grid;
  grid-template-columns: ${props =>
    props.isAvatarFirst ? '30px 1fr' : '1fr 30px'};
  column-gap: 8px;
  justify-items: ${props => (props.isAvatarFirst ? 'flex-start' : 'flex-end')};
  border: none;
  cursor: pointer;
  padding: 0;
  min-width: 0;

  @media (max-width: 360px) {
    column-gap: 6px;
  }
`

const AvatarImage = styled.img<{ isAvatarFirst: boolean }>`
  width: 30px;
  height: 30px;
  object-fit: cover;
  grid-row: 1 / 3;
  grid-column: ${props => (props.isAvatarFirst ? '1 / 2' : '2 / 3')};
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid ${colors.grey900};
  justify-self: ${props => (props.isAvatarFirst ? 'flex-start' : 'flex-end')};
`
